import type { Company } from "@station/contracts";
import {
  Alert,
  type BreadcrumbItem,
  Button,
  EmptyState,
  PageHeader,
  Select,
  SkeletonRows,
  useToast,
} from "@station/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { Building2, ListTree } from "lucide-react";
import { type ReactElement, type ReactNode, useEffect, useId } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { api } from "../../lib/api.js";
import { errorMessage } from "../../lib/errors.js";
import { useDocumentTitle } from "../../lib/hooks.js";
import { queryKeys } from "../../lib/query.js";
import { useMe, usePermissions } from "../../lib/session.js";

/** Tüm muhasebe rotalarının arama şemasına eklenir: seçili şirket URL'de taşınır (paylaşılabilir bağlantı). */
export const companySearchShape = { company: z.string().uuid().optional().catch(undefined) };

const storageKey = (tenantId: string) => `station.accounting.company.${tenantId}`;

function readStored(tenantId: string | null): string | null {
  try {
    return tenantId ? localStorage.getItem(storageKey(tenantId)) : null;
  } catch {
    return null;
  }
}

export interface AccountingCompany {
  /** Çözümlenen şirket; şirketler yüklenene kadar ya da hiç şirket yoksa null. */
  company: Company | null;
  companyId: string | null;
  companies: readonly Company[];
  isLoading: boolean;
  error: unknown;
  /** Birden çok şirket varsa seçici gösterilir; tek şirkette arayüz sadeleşir. */
  showPicker: boolean;
  select: (companyId: string) => void;
}

/**
 * Defter ŞİRKET bazındadır: tüm muhasebe uçları `companyId` ister. Seçim sırası: URL (`?company=`) → bu kiracıda son
 * seçilen (localStorage) → ilk şirket. Tüm muhasebe sayfaları bu kancayı kullanır; seçim sayfalar arasında korunur.
 */
export function useAccountingCompany(fixedCompanyId?: string): AccountingCompany {
  const tenantId = useMe().data?.activeTenant?.id ?? null;
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { company?: string };
  const query = useQuery({
    queryKey: queryKeys.companies,
    queryFn: async () => (await api.GET("/companies")).data!.items as Company[],
  });
  const companies = (query.data ?? []).filter((item) => item.isActive);
  const find = (id: string | null | undefined) => companies.find((item) => item.id === id) ?? null;
  // `fixedCompanyId`: fiş detayı gibi şirketi VERİDEN belli olan sayfalar; seçici gösterilmez, seçim o şirkete çekilir.
  const company = fixedCompanyId
    ? find(fixedCompanyId)
    : (find(search.company) ?? find(readStored(tenantId)) ?? companies[0] ?? null);

  useEffect(() => {
    if (!tenantId || !company) return;
    try {
      localStorage.setItem(storageKey(tenantId), company.id);
    } catch {
      // depolama kapalıysa seçim yalnızca URL'de yaşar
    }
  }, [tenantId, company]);

  return {
    company,
    companyId: company?.id ?? null,
    companies,
    isLoading: query.isPending,
    error: query.error,
    showPicker: companies.length > 1 && !fixedCompanyId,
    select: (companyId) =>
      void navigate({
        to: ".",
        search: ((previous: Record<string, unknown>) => ({ ...previous, company: companyId })) as never,
        replace: true,
      }),
  };
}

export function companyLabel(company: Pick<Company, "legalName" | "shortName">): string {
  return company.shortName ?? company.legalName;
}

export function CompanyPicker({ accounting }: { accounting: AccountingCompany }): ReactElement | null {
  const { t } = useTranslation();
  const id = useId();
  if (!accounting.company || accounting.companies.length <= 1) return null;
  if (!accounting.showPicker)
    return (
      <span className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground">
        <Building2 className="size-4" aria-hidden="true" />
        {companyLabel(accounting.company)}
      </span>
    );
  return (
    <div className="flex items-center gap-2 print:hidden">
      <label htmlFor={id} className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground">
        <Building2 className="size-4" aria-hidden="true" />
        {t("accounting.company.label")}
      </label>
      <Select
        id={id}
        className="h-9 w-56"
        value={accounting.company.id}
        onChange={(event) => accounting.select(event.target.value)}
      >
        {accounting.companies.map((item) => (
          <option key={item.id} value={item.id}>
            {companyLabel(item)}
          </option>
        ))}
      </Select>
    </div>
  );
}

export interface AccountingContext {
  companyId: string;
  company: Company;
}

/**
 * Muhasebe sayfalarının ortak çerçevesi: başlık + şirket seçici + "şirket yok / hesap planı kurulmamış" durumları.
 * İçerik yalnızca şirket çözümlenip hesap planı kurulduktan sonra çizilir; sayfalar `companyId`'yi hazır alır.
 */
export function AccountingPage({
  title,
  description,
  breadcrumb,
  actions,
  fixedCompanyId,
  children,
}: {
  /** Şirketi veriden belli olan sayfalar (fiş detayı/düzenleme): seçici yerine şirket adı gösterilir. */
  fixedCompanyId?: string;
  title: string;
  description?: string;
  /** "Muhasebe" kökünden SONRAKİ öğeler; verilmezse yalnızca sayfa başlığı eklenir. */
  breadcrumb?: BreadcrumbItem[];
  actions?: ReactNode | ((context: AccountingContext) => ReactNode);
  children: (context: AccountingContext) => ReactNode;
}): ReactElement {
  const { t } = useTranslation();
  const accounting = useAccountingCompany(fixedCompanyId);
  useDocumentTitle(title);
  const { company } = accounting;
  const settings = useQuery({
    queryKey: queryKeys.accounting(company?.id ?? "none", "settings"),
    enabled: company !== null,
    queryFn: async () =>
      (await api.GET("/accounting/companies/{id}/settings", { params: { path: { id: company!.id } } })).data!,
  });
  const ready = company !== null && settings.data?.isInitialized === true;
  const context = ready ? { companyId: company.id, company } : null;
  const error = accounting.error ?? settings.error;

  return (
    <>
      <PageHeader
        title={title}
        {...(description ? { description } : {})}
        breadcrumb={[{ label: t("menu.groups.accounting") }, ...(breadcrumb ?? [{ label: title }])]}
        actions={
          <>
            <CompanyPicker accounting={accounting} />
            {context ? (typeof actions === "function" ? actions(context) : actions) : null}
          </>
        }
      />
      {error ? (
        <Alert tone="danger">{errorMessage(error)}</Alert>
      ) : accounting.isLoading || (company !== null && settings.isPending) ? (
        <SkeletonRows rows={6} />
      ) : company === null ? (
        <EmptyState
          icon={<Building2 />}
          title={t("accounting.company.noneTitle")}
          description={t("accounting.company.noneDescription")}
        />
      ) : context ? (
        // Şirket değişince sayfanın yerel durumu (seçimler, açık paneller) sıfırlanır.
        <div key={context.companyId}>{children(context)}</div>
      ) : (
        <InitializeChart company={company} />
      )}
    </>
  );
}

function InitializeChart({ company }: { company: Company }): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const canManage = usePermissions().has("accounting.chart.manage");
  const initialize = useMutation({
    mutationFn: async () =>
      api.POST("/accounting/companies/{id}/initialize", { params: { path: { id: company.id } } }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.accounting(company.id) });
      toast({ tone: "success", title: t("accounting.company.initialized") });
    },
    onError: (error) => toast({ tone: "danger", title: errorMessage(error) }),
  });
  return (
    <EmptyState
      icon={<ListTree />}
      title={t("accounting.company.notInitializedTitle")}
      description={t("accounting.company.notInitializedDescription", { company: companyLabel(company) })}
      action={
        canManage ? (
          <Button onClick={() => initialize.mutate()} loading={initialize.isPending}>
            {t("accounting.company.initialize")}
          </Button>
        ) : (
          <p className="text-sm text-muted-foreground">{t("accounting.company.initializeNoPermission")}</p>
        )
      }
    />
  );
}
